import React, { useCallback } from 'react';
import { Grid2 as Grid, Typography } from '@mui/material';
import { defaultsDeep } from 'lodash';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { useFormContext } from 'framework/form';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  ProjectSectionAccordion,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import AddAcceptanceArtifactModal from './AddAcceptanceArtifactModal';
import {
  AcceptanceArtifactDateField,
  AcceptanceArtifactLabelField,
  AcceptanceArtifactStorageFileField,
  AcceptanceArtifactStorageTypeIcon,
  EditAcceptanceDocumentActionButton,
} from './fields';
import { EditableListDataGridLayout, EDITABLE_LIST_CUSTOM, EDITABLE_LIST_FIELD } from 'framework/list';
import { useLocation } from 'react-router-dom';
import { AnchorField, getAnchorRowStyle } from 'framework/ui/field';

type AcceptanceArtifactActionsContext = {
  canManage: boolean;
};

type AcceptanceArtifactsRefreshContext = {
  onSuccess: () => Promise<void>;
};

type AcceptanceArtifactActionConfig = {
  Component: React.ComponentType<any>;
  isVisible: (ctx: AcceptanceArtifactActionsContext) => boolean;
  getComponentProps?: (ctx: AcceptanceArtifactsRefreshContext) => Record<string, unknown>;
};

const ACCEPTANCE_ARTIFACT_ACTIONS: AcceptanceArtifactActionConfig[] = [
  {
    Component: AddAcceptanceArtifactModal,
    isVisible: ctx => ctx.canManage,
    getComponentProps: ({ onSuccess }) => ({ onSuccess }),
  },
];

const AcceptanceArtifactsList = () => {
  const { isAccessGranted } = useProjectSecurityContext();
  const canManage = isAccessGranted(ACCESS_INFO_SECTIONS.CLIENT_CONFIRMATION, ACCESS_INFO_ACTIONS.MANAGE);
  const actionsContext: AcceptanceArtifactActionsContext = { canManage };
  const visibleActions = ACCEPTANCE_ARTIFACT_ACTIONS.filter(({ isVisible }) => isVisible(actionsContext));
  const { submitForm } = useFormContext();
  const { hash } = useLocation();

  const handleModalSuccess = useCallback(async () => {
    submitForm?.();
  }, [submitForm]);

  return (
    <ProjectSectionAccordion
      defaultExpanded
      title={
        <Grid container direction="row" justifyContent="space-between" alignItems="center" width="100%" pl={2}>
          <Grid size={6}>
            <Typography variant="h4" color="primary">
              Acceptance artifacts
            </Typography>
          </Grid>
          <Grid size="auto" alignItems="center" sx={{ '.filter-item-wrapper': { m: 0 } }} />
          <Grid size="grow" />
          <Grid size="auto" display="flex" alignItems="center" gap={0.5}>
            {visibleActions.map(({ Component, getComponentProps }) => (
              <Component key={Component.name} {...(getComponentProps?.({ onSuccess: handleModalSuccess }) ?? {})} />
            ))}
          </Grid>
        </Grid>
      }
    >
      <EditableListDataGridLayout
        disabledAddNewRow
        rowProps={data =>
          defaultsDeep(getAnchorRowStyle(data, hash), {
            sx: {
              padding: 0,
              px: 1,
              alignItems: 'center',
              height: '56px',
            },
          })
        }
        getTableCells={async () =>
          Promise.resolve([
            {
              type: EDITABLE_LIST_FIELD,
              source: 'index',
              isEditor: false,
              component: AnchorField,
              componentProps: {
                label: '',
                sx: {
                  fontWeight: 500,
                  color: 'text.secondary',
                },
              },
              gridCellProps: { size: 0.5 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'metadata.label',
              isEditor: false,
              component: AcceptanceArtifactLabelField,
              componentProps: { label: '' },
              gridCellProps: { size: 3 },
            },
            {
              type: EDITABLE_LIST_CUSTOM,
              isEditor: false,
              component: AcceptanceArtifactStorageTypeIcon,
              componentProps: {},
              gridCellProps: { size: 0.5, display: 'flex', alignItems: 'center' },
            },
            {
              type: EDITABLE_LIST_CUSTOM,
              isEditor: false,
              component: AcceptanceArtifactStorageFileField,
              componentProps: {},
              gridCellProps: { size: 3 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'metadata.createdAt',
              isEditor: false,
              component: AcceptanceArtifactDateField,
              componentProps: {
                label: '',
              },
              gridCellProps: { size: 2 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'metadata.updatedBy',
              isEditor: false,
              component: EmployeeLinkFieldWithNameLoading,
              componentProps: {
                label: '',
                sx: { color: 'text.secondary' },
              },
              gridCellProps: { size: 2 },
            },
            {
              type: EDITABLE_LIST_CUSTOM,
              isEditor: false,
              component: EditAcceptanceDocumentActionButton,
              componentProps: {
                onSuccess: handleModalSuccess,
                canManage,
              },
              gridCellProps: { size: 1, display: 'flex', justifyContent: 'flex-end' },
            },
          ])
        }
      />
    </ProjectSectionAccordion>
  );
};

export default AcceptanceArtifactsList;
